import { c as _c } from "react/compiler-runtime";
import { isEmpty } from '@newfront-insurance/core';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useMemo, useRef } from 'react';
import { OrphanedStreamRecovery } from './orphaned-stream-recovery';
import type { GetChatsByFiltersChat } from '../../../queries/chats';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { NovaChatProvider } from '../providers';
interface OrphanedStreamsContainerProps {
  chats: GetChatsByFiltersChat[];
}

/**
 * Container component that manages recovery of orphaned streams.
 * Detects chats with activeStreamId (excluding the currently active chat)
 * and creates renderless recovery components for each one.
 *
 * Each orphaned stream gets its own NovaChatProvider instance to ensure
 * proper isolation and stream resumability.
 */
export function OrphanedStreamsContainer({
  chats
}: OrphanedStreamsContainerProps) {
  const {
    chatUuid,
    userUuid,
    authProvider,
    userScopes,
    notificationController
  } = useChatOverlayContext();
  const chatsRef = useRef(chats);

  // Find orphaned streams: chats with activeStreamId that are not the current chat
  const orphanedStreams = useMemo(() => {
    return chatsRef.current.filter(chat => {
      if (!chat.uuid) {
        return false;
      }
      const hasActiveStream = chat.activeStreamId !== null;
      const chatUuidWithoutPrefix = chatUuid?.replace('new-chat-', '');
      const isNotCurrentChat = chat.uuid !== chatUuidWithoutPrefix;
      return hasActiveStream && isNotCurrentChat;
    });
  }, [chatUuid]);

  // Don't render anything if no orphaned streams or missing required context
  if (orphanedStreams.length === 0 || isEmpty(userUuid) || !authProvider) {
    return null;
  }
  browserLogger.debug('[Orphaned Streams Container] Found orphaned streams', {
    count: orphanedStreams.length,
    chatUuids: orphanedStreams.map(chat_0 => chat_0.uuid)
  });
  return <>
      {orphanedStreams.map(chat_1 => <OrphanedStreamProvider key={chat_1.uuid} chat={chat_1} userUuid={userUuid} authProvider={authProvider} scopes={userScopes} onOrphanedStreamFinish={notificationController.onOrphanedStreamFinish} />)}
    </>;
}

/**
 * Provider wrapper for a single orphaned stream.
 * Fetches initial messages and wraps the recovery component in NovaChatProvider.
 */
function OrphanedStreamProvider(t0) {
  const $ = _c(12);
  const {
    chat,
    userUuid,
    authProvider,
    scopes,
    onOrphanedStreamFinish
  } = t0;
  const chatTitle = chat.title || "New Chat";
  const t1 = chat.accountUuid ?? undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== chat.uuid || $[3] !== chatTitle || $[4] !== onOrphanedStreamFinish) {
    t4 = <OrphanedStreamRecovery chatUuid={chat.uuid} chatTitle={chatTitle} initialMessages={t3} onStreamFinish={onOrphanedStreamFinish} />;
    $[2] = chat.uuid;
    $[3] = chatTitle;
    $[4] = onOrphanedStreamFinish;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== authProvider || $[7] !== scopes || $[8] !== t1 || $[9] !== t4 || $[10] !== userUuid) {
    t5 = <NovaChatProvider userUuid={userUuid} accountUuid={t1} authProvider={authProvider} scopes={scopes} initialMessages={t2}>{t4}</NovaChatProvider>;
    $[6] = authProvider;
    $[7] = scopes;
    $[8] = t1;
    $[9] = t4;
    $[10] = userUuid;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}